/**
 * Debug Test Message Sending
 * Diagnose why test messages aren't being created
 */

import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import MessageService from '../services/communication/MessageService';
import { createAction } from '../models/Message';

export async function debugTestMessages() {
  console.log('🔍 DEBUGGING TEST MESSAGE SYSTEM\n');
  console.log('═══════════════════════════════════════════════════\n');

  try {
    // 1. Check SchoolContext state
    const schoolContext = window.__REACT_DEVTOOLS_GLOBAL_HOOK__?.renderers?.get(1)?.getCurrentFiber?.();
    console.log('1️⃣ Checking SchoolContext...');

    // Try to get schoolId from localStorage (SchoolContext cache)
    const cachedSchool = localStorage.getItem('swan_school_context');
    const schoolData = cachedSchool ? JSON.parse(cachedSchool) : null;
    const schoolId = schoolData?.schoolId;

    console.log(`   schoolId: ${schoolId || '❌ NOT SET'}`);

    if (!schoolId) {
      console.error('❌ FATAL: No schoolId found - cannot send messages');
      return;
    }

    // 2. Check students data
    console.log('\n2️⃣ Checking students data...');
    const cachedStudents = sessionStorage.getItem('schoolData_students');
    const students = cachedStudents ? JSON.parse(cachedStudents) : [];
    console.log(`   Students loaded: ${students.length}`);

    if (students.length === 0) {
      console.error('❌ FATAL: No students found - cannot send messages');
      return;
    }

    console.log(`   Sample student: ${students[0]?.firstName} ${students[0]?.surname} (${students[0]?.id})`);

    // 3. Check user auth
    console.log('\n3️⃣ Checking authentication...');
    const userAuth = localStorage.getItem('swan_user_cache');
    const user = userAuth ? JSON.parse(userAuth) : null;
    console.log(`   User: ${user?.email || '❌ NOT LOGGED IN'}`);
    console.log(`   User ID: ${user?.uid || '❌ NO UID'}`);

    if (!user?.uid) {
      console.error('❌ FATAL: Not authenticated');
      return;
    }

    // 4. Check parent links for first student
    console.log('\n4️⃣ Checking parent links...');
    const testStudent = students[0];
    const linksQuery = query(
      collection(db, 'parentChildLinks'),
      where('studentId', '==', testStudent.id),
      where('status', '==', 'active')
    );

    const linksSnapshot = await getDocs(linksQuery);
    console.log(`   Active parent links for ${testStudent.firstName}: ${linksSnapshot.size}`);

    if (linksSnapshot.empty) {
      console.warn('⚠️  No parent links found for first student');
    } else {
      linksSnapshot.forEach(doc => {
        const linkData = doc.data();
        console.log(`     - Parent: ${linkData.parentId.substring(0, 12)}... (${linkData.telegram?.chatId ? 'has Telegram' : 'no Telegram'})`);
      });
    }

    // 5. Count total parent links
    console.log('\n5️⃣ Counting all parent links...');
    const allLinksQuery = query(
      collection(db, 'parentChildLinks'),
      where('status', '==', 'active')
    );
    const allLinksSnapshot = await getDocs(allLinksQuery);
    console.log(`   Total active parent links: ${allLinksSnapshot.size}`);

    // 6. Test single message creation
    console.log('\n6️⃣ Testing single message creation...');
    console.log('   This will send ONE test message to the first parent link found');
    console.log('   Proceeding in 3 seconds... (refresh page to cancel)\n');

    await new Promise(resolve => setTimeout(resolve, 3000));

    if (allLinksSnapshot.size > 0) {
      const firstLink = allLinksSnapshot.docs[0].data();
      const testStudentId = firstLink.studentId;
      const testParentId = firstLink.parentId;

      console.log(`   Sending to: parent ${testParentId.substring(0, 12)}... about student ${testStudentId}`);

      const confirmAction = createAction(
        'debug_test',
        'acknowledge',
        '✅ Debug test received'
      );

      try {
        const messageId = await MessageService.createMessage({
          schoolId,
          parentId: testParentId,
          studentId: testStudentId,
          type: 'general_announcement',
          priority: 'normal',
          content: {
            subject: '🔧 Debug Test Message',
            body: 'This is a debug test message sent at ' + new Date().toLocaleString() + '.\n\nIf you received this, the messaging system is working correctly.',
            summary: 'Debug test - safe to ignore'
          },
          actions: [confirmAction],
          metadata: {
            category: 'system_test',
            targetAudience: 'debug_single',
            testType: 'debug_diagnostic'
          },
          createdBy: user.uid
        });

        console.log(`   ✅ SUCCESS! Message created: ${messageId}`);
        console.log(`   Check Firestore: schools/${schoolId}/messages/${messageId}`);

        return { success: true, messageId };

      } catch (error) {
        console.error(`   ❌ FAILED to create message:`, error);
        console.error(`   Error details:`, error.message);
        console.error(`   Stack:`, error.stack);

        return { success: false, error: error.message };
      }
    } else {
      console.error('❌ No parent links to test with');
    }

  } catch (error) {
    console.error('❌ Debug failed:', error);
    console.error('Stack:', error.stack);
  }
}

// Make available globally
if (typeof window !== 'undefined') {
  window.debugTestMessages = debugTestMessages;
}
